/**
 * Admin Access Page - Manage user roles and permissions
 */

import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/layout";
import { PageLoader } from "@/components/PageLoader";
import {
  IconShield,
  IconUser,
  IconSearch,
  IconPlus,
  IconX,
  IconChevronRight,
} from "@/components/Icons";
import { getTelegram } from "@/lib/telegram";
import { useSearchAccounts } from "../shared/hooks/useSearchAccounts";
import {
  useAdminRoles,
  useAdminPermissions,
  useAdminUsersWithAccess,
  useAdminUserAccess,
  useAssignRole,
  useRevokeRole,
  useGrantPermission,
  useRevokePermission,
  type UserWithAccess,
  type PermissionInfo,
} from "./hooks";

type ViewMode = "users" | "roles" | "permissions";

function formatName(user: { firstName: string | null; lastName: string | null; username: string | null }): string {
  const parts = [user.firstName, user.lastName].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  return user.username || "Без имени";
}

// Group permissions by category
function groupByCategory(permissions: PermissionInfo[]): Record<string, PermissionInfo[]> {
  return permissions.reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {} as Record<string, PermissionInfo[]>);
}

const CATEGORY_LABELS: Record<string, string> = {
  events: "Мероприятия",
  payments: "Платежи",
  tickets: "Билеты",
  home_groups: "Домашние группы",
  ministries: "Служения",
  users: "Пользователи",
  raffle: "Розыгрыши",
  admin: "Админ",
};

export function AdminAccessPage() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const [viewMode, setViewMode] = useState<ViewMode>("users");
  const [selectedUser, setSelectedUser] = useState<UserWithAccess | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [showAddPermissionModal, setShowAddPermissionModal] = useState(false);

  // Data hooks
  const { roles, isLoading: rolesLoading } = useAdminRoles();
  const { permissions, isLoading: permissionsLoading } = useAdminPermissions();
  const { users, isLoading: usersLoading, refetch: refetchUsers } = useAdminUsersWithAccess();
  
  // User search
  const { accounts: searchResults, isLoading: isSearching } = useSearchAccounts({
    query: searchQuery,
    limit: 10,
  });
  
  // Selected user details
  const { user: userDetails, refetch: refetchUserDetails } = useAdminUserAccess(
    selectedUser?.id ?? null
  );

  // Mutations
  const { assignRole } = useAssignRole();
  const { revokeRole } = useRevokeRole();
  const { grantPermission } = useGrantPermission();
  const { revokePermission } = useRevokePermission();

  // Telegram back button
  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (selectedUser) {
        setSelectedUser(null);
      } else {
        navigate("/admin");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate, selectedUser]);

  // Handlers
  const handleSelectUser = useCallback((user: UserWithAccess) => {
    setSelectedUser(user);
    setSearchQuery("");
  }, []);

  const handleAssignRole = useCallback(
    async (roleCode: string) => {
      if (!selectedUser) return;
      const result = await assignRole(selectedUser.id, roleCode);
      if (result.success) {
        refetchUserDetails();
        refetchUsers();
        setShowAddRoleModal(false);
      }
      // Show toast/alert
      alert(result.message || (result.success ? "Роль назначена" : "Ошибка"));
    },
    [selectedUser, assignRole, refetchUserDetails, refetchUsers]
  );

  const handleRevokeRole = useCallback(
    async (roleCode: string) => {
      if (!selectedUser) return;
      if (!confirm(`Отозвать роль?`)) return;
      const result = await revokeRole(selectedUser.id, roleCode);
      if (result.success) {
        refetchUserDetails();
        refetchUsers();
      }
      alert(result.message || (result.success ? "Роль отозвана" : "Ошибка"));
    },
    [selectedUser, revokeRole, refetchUserDetails, refetchUsers]
  );

  const handleGrantPermission = useCallback(
    async (permissionCode: string) => {
      if (!selectedUser) return;
      const result = await grantPermission(selectedUser.id, permissionCode);
      if (result.success) {
        refetchUserDetails();
        refetchUsers();
        setShowAddPermissionModal(false);
      }
      alert(result.message || (result.success ? "Разрешение выдано" : "Ошибка"));
    },
    [selectedUser, grantPermission, refetchUserDetails, refetchUsers]
  );

  const handleRevokePermission = useCallback(
    async (permissionCode: string) => {
      if (!selectedUser) return;
      if (!confirm(`Отозвать разрешение?`)) return;
      const result = await revokePermission(selectedUser.id, permissionCode);
      if (result.success) {
        refetchUserDetails();
        refetchUsers();
      }
      alert(result.message || (result.success ? "Разрешение отозвано" : "Ошибка"));
    },
    [selectedUser, revokePermission, refetchUserDetails, refetchUsers]
  );

  const isLoading = rolesLoading || permissionsLoading || usersLoading;

  if (isLoading) {
    return (
      <Layout>
        <PageLoader message="Загрузка..." />
      </Layout>
    );
  }

  // User detail view
  if (selectedUser) {
    const currentUser = userDetails || selectedUser;
    const assignedRoleCodes = new Set(currentUser.roles.map((r) => r.roleCode));
    const assignedPermissionCodes = new Set(
      currentUser.directPermissions.filter((p) => p.granted).map((p) => p.permissionCode)
    );

    return (
      <Layout>
        <div className="min-h-screen bg-slate-50">
          {/* Header */}
          <div className="bg-white border-b border-slate-200 px-4 py-4 sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <IconUser className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg font-semibold text-slate-900 truncate">
                  {formatName(currentUser)}
                </h1>
                {currentUser.username && (
                  <p className="text-sm text-slate-500">@{currentUser.username}</p>
                )}
              </div>
            </div>
          </div>

          <div className="p-4 space-y-6">
            {/* Roles section */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                  Роли
                </h2>
                <button
                  onClick={() => setShowAddRoleModal(true)}
                  className="flex items-center gap-1 text-sm text-blue-600 font-medium"
                >
                  <IconPlus className="w-4 h-4" />
                  Добавить
                </button>
              </div>

              {currentUser.roles.length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center bg-white rounded-xl">
                  Нет назначенных ролей
                </p>
              ) : (
                <div className="bg-white rounded-xl divide-y divide-slate-100">
                  {currentUser.roles.map((role) => (
                    <div
                      key={role.id}
                      className="flex items-center justify-between px-4 py-3"
                    >
                      <div>
                        <p className="font-medium text-slate-900">{role.roleName}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(role.grantedAt).toLocaleDateString("ru-RU")}
                          {role.expiresAt && (
                            <span className="text-amber-600">
                              {" "}
                              · до {new Date(role.expiresAt).toLocaleDateString("ru-RU")}
                            </span>
                          )}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRevokeRole(role.roleCode)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <IconX className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Direct permissions section */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                  Прямые разрешения
                </h2>
                <button
                  onClick={() => setShowAddPermissionModal(true)}
                  className="flex items-center gap-1 text-sm text-blue-600 font-medium"
                >
                  <IconPlus className="w-4 h-4" />
                  Добавить
                </button>
              </div>

              {currentUser.directPermissions.filter((p) => p.granted).length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center bg-white rounded-xl">
                  Нет прямых разрешений
                </p>
              ) : (
                <div className="bg-white rounded-xl divide-y divide-slate-100">
                  {currentUser.directPermissions
                    .filter((p) => p.granted)
                    .map((perm) => (
                      <div
                        key={perm.id}
                        className="flex items-center justify-between px-4 py-3"
                      >
                        <div>
                          <p className="font-medium text-slate-900">{perm.permissionName}</p>
                          <p className="text-xs text-slate-500">
                            {perm.permissionCode}
                            {perm.reason && <span> · {perm.reason}</span>}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRevokePermission(perm.permissionCode)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <IconX className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </section>
          </div>

          {/* Add Role Modal */}
          {showAddRoleModal && (
            <div className="fixed inset-0 bg-black/50 flex items-end z-50">
              <div className="bg-white w-full rounded-t-3xl max-h-[80vh] overflow-hidden animate-slideUp">
                <div className="flex items-center justify-between p-4 border-b">
                  <h3 className="text-lg font-semibold">Добавить роль</h3>
                  <button onClick={() => setShowAddRoleModal(false)}>
                    <IconX className="w-6 h-6 text-slate-400" />
                  </button>
                </div>
                <div className="p-4 overflow-y-auto max-h-[60vh]">
                  {roles
                    .filter((r) => !assignedRoleCodes.has(r.code))
                    .map((role) => (
                      <button
                        key={role.id}
                        onClick={() => handleAssignRole(role.code)}
                        className="w-full text-left px-4 py-3 rounded-xl hover:bg-slate-50 active:bg-slate-100 transition-colors mb-2"
                      >
                        <p className="font-medium text-slate-900">{role.name}</p>
                        <p className="text-sm text-slate-500">{role.description}</p>
                      </button>
                    ))}
                  {roles.filter((r) => !assignedRoleCodes.has(r.code)).length === 0 && (
                    <p className="text-center text-slate-500 py-4">
                      Все роли уже назначены
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Add Permission Modal */}
          {showAddPermissionModal && (
            <div className="fixed inset-0 bg-black/50 flex items-end z-50">
              <div className="bg-white w-full rounded-t-3xl max-h-[80vh] overflow-hidden animate-slideUp">
                <div className="flex items-center justify-between p-4 border-b">
                  <h3 className="text-lg font-semibold">Добавить разрешение</h3>
                  <button onClick={() => setShowAddPermissionModal(false)}>
                    <IconX className="w-6 h-6 text-slate-400" />
                  </button>
                </div>
                <div className="p-4 overflow-y-auto max-h-[60vh]">
                  {Object.entries(groupByCategory(permissions)).map(([category, perms]) => {
                    const availablePerms = perms.filter(
                      (p) => !assignedPermissionCodes.has(p.code)
                    );
                    if (availablePerms.length === 0) return null;

                    return (
                      <div key={category} className="mb-4">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                          {CATEGORY_LABELS[category] || category}
                        </p>
                        {availablePerms.map((perm) => (
                          <button
                            key={perm.id}
                            onClick={() => handleGrantPermission(perm.code)}
                            className="w-full text-left px-4 py-3 rounded-xl hover:bg-slate-50 active:bg-slate-100 transition-colors mb-1"
                          >
                            <p className="font-medium text-slate-900">{perm.name}</p>
                            <p className="text-xs text-slate-500">{perm.code}</p>
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </Layout>
    );
  }

  // Main list view
  return (
    <Layout>
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-4 py-4 sticky top-0 z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <IconShield className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-slate-900">Доступы</h1>
              <p className="text-sm text-slate-500">Роли и разрешения пользователей</p>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Поиск пользователя..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* View mode tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode("users")}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                viewMode === "users"
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              Пользователи
            </button>
            <button
              onClick={() => setViewMode("roles")}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                viewMode === "roles"
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              Роли
            </button>
            <button
              onClick={() => setViewMode("permissions")}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                viewMode === "permissions"
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              Разрешения
            </button>
          </div>
        </div>

        {/* Search results overlay */}
        {searchQuery && (
          <div className="p-4">
            {isSearching ? (
              <div className="text-center py-8 text-slate-500">Поиск...</div>
            ) : searchResults.length === 0 ? (
              <div className="text-center py-8 text-slate-500">Ничего не найдено</div>
            ) : (
              <div className="bg-white rounded-xl divide-y divide-slate-100">
                {searchResults.map((account) => (
                  <button
                    key={account.id}
                    onClick={() =>
                      handleSelectUser({
                        id: account.id,
                        firstName: account.firstName,
                        lastName: account.lastName,
                        username: account.username,
                        telegramId: "",
                        roles: [],
                        directPermissions: [],
                      })
                    }
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 active:bg-slate-100 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                      <IconUser className="w-5 h-5 text-slate-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">
                        {formatName(account)}
                      </p>
                      {account.username && (
                        <p className="text-sm text-slate-500">@{account.username}</p>
                      )}
                    </div>
                    <IconChevronRight className="w-5 h-5 text-slate-300" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Content based on view mode */}
        {!searchQuery && (
          <div className="p-4">
            {viewMode === "users" && (
              <div className="space-y-3">
                {users.length === 0 ? (
                  <div className="text-center py-12">
                    <IconUser className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Нет пользователей с доступами</p>
                  </div>
                ) : (
                  users.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => handleSelectUser(user)}
                      className="w-full bg-white rounded-xl p-4 text-left shadow-sm hover:shadow transition-shadow"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                          <IconUser className="w-5 h-5 text-slate-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">
                            {formatName(user)}
                          </p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {user.roles.map((role) => (
                              <span
                                key={role.id}
                                className="px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full"
                              >
                                {role.roleName}
                              </span>
                            ))}
                            {user.directPermissions.filter((p) => p.granted).length > 0 && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                                +{user.directPermissions.filter((p) => p.granted).length} разр.
                              </span>
                            )}
                          </div>
                        </div>
                        <IconChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}

            {viewMode === "roles" && (
              <div className="space-y-3">
                {roles.map((role) => (
                  <div key={role.id} className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-slate-900">{role.name}</h3>
                      {role.isSystem && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-500 rounded-full">
                          Системная
                        </span>
                      )}
                    </div>
                    {role.description && (
                      <p className="text-sm text-slate-500 mb-2">{role.description}</p>
                    )}
                    <div className="flex flex-wrap gap-1">
                      {role.permissions.map((perm) => (
                        <span
                          key={perm.id}
                          className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded"
                        >
                          {perm.code}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {viewMode === "permissions" && (
              <div className="space-y-4">
                {Object.entries(groupByCategory(permissions)).map(([category, perms]) => (
                  <div key={category}>
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 px-1">
                      {CATEGORY_LABELS[category] || category}
                    </h3>
                    <div className="bg-white rounded-xl divide-y divide-slate-100">
                      {perms.map((perm) => (
                        <div key={perm.id} className="px-4 py-3">
                          <p className="font-medium text-slate-900">{perm.name}</p>
                          <p className="text-xs text-slate-500">{perm.code}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </Layout>
  );
}

export default AdminAccessPage;
